import React from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertExecution from '@app/model/alert/AlertExecution';
import { useQuery } from '@tanstack/react-query';
import AlertService from '@app/model/alert/service/AlertService';
import { AlertExecutionQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import { Link } from 'react-router-dom';
import Utilities from '@util/Utilities';
import _isEmpty from 'lodash/isEmpty';
import Message from '@app/component/message/Message';
import CompetitorsSoldOutAlertExecutionNotificationFragment
	from '@app/site/secured/rateshopper/rates/alert-execution/fragment/competitors-sold-out/CompetitorsSoldOutAlertExecutionNotificationFragment';
import { CompetitorsSoldOutAlertRuleInclusion } from '@app/model/alert/CompetitorsSoldOutAlertRuleInclusion';

const CompetitorsSoldOutAlertExecutionFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	alertExecution: AlertExecution,
}> = ({ companyHotel, alertExecution }) => {

	const { data: notificationOutputs, isLoading, isSuccess } = useQuery({
		queryFn: () => AlertService.findCompetitorsSoldOutAlertExecutionNotifications(companyHotel.id, alertExecution.id),
		queryKey: AlertExecutionQueryKeys.notifications(alertExecution.id),
	});

	const formatDays = (days: number, prefix: 'Next' | 'Last') => {
		return `${prefix} ${days} ${days === 1 ? 'Day' : 'Days'}`;
	};

	const formatCompetitorsInclusion = () => {
		const rule = alertExecution.alertOutput.competitorsSoldOutAlertRule;
		if (rule.competitorsInclusion === CompetitorsSoldOutAlertRuleInclusion.SOME) {
			return `${rule.inclusionPercentage}%`;
		}

		return Utilities.snakeCaseToWords(rule.competitorsInclusion);
	};

	const formatRoomTypes = () => {
		const roomTypes = alertExecution.alertOutput.alert.filter.roomTypes;
		if (_isEmpty(roomTypes)) return 'All Room Types';

		return roomTypes.map(roomType => companyHotel.findRoomMappingTypeName(roomType)).join(', ');
	};

	if (isLoading) {
		return <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
			{[...Array(3)].map((x, i) =>
				<div key={i} className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
					<div className="w-full animate-pulse space-y-4">
						<div className="h-4 w-36 rounded-full bg-gray-200"></div>
						<div className="h-3 w-24 rounded-full bg-gray-200"></div>
						<div className="h-20 rounded-lg bg-gray-100"></div>
					</div>
				</div>,
			)}
		</div>;
	}

	if (isSuccess) {
		const propertyName = companyHotel.mainHotel?.competitors?.[0]?.hotel?.name ?? companyHotel.mainHotel?.name ?? 'My Property';
		const timeframeDays = alertExecution.alertOutput.alert.timeframe.days;

		return <>
			<main className="bg-gray-50">
				<div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
					<div className="text-right mb-1 mr-1">
					<span
						className="text-xs text-gray-400">Executed at {Utilities.formatDateString(alertExecution.executionDate, 'DD/MM/YYYY HH:mm')}</span>
					</div>
					{!!alertExecution.alertOutput.alert &&
						<section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
							<div className="flex flex-col gap-1">
								<h1 className="text-lg font-semibold leading-6 text-gray-950">{alertExecution.alertOutput.alert.name}</h1>
								<p className="text-xs font-medium text-gray-500">{propertyName}</p>
							</div>

							<div className="mt-7">
								<p className="text-sm font-semibold text-gray-900">Configuration</p>
								<dl className="mt-4 grid grid-cols-1 gap-x-16 gap-y-5 text-sm sm:grid-cols-2">
									<div>
										<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Type</dt>
										<dd className="mt-1 text-gray-800">Competitors Sold Out</dd>
									</div>
									<div>
										<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Dates</dt>
										<dd className="mt-1 text-gray-800">{formatDays(timeframeDays, 'Next')}</dd>
									</div>
									<div>
										<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Include Competitors</dt>
										<dd className="mt-1 text-gray-800">{formatCompetitorsInclusion()}</dd>
									</div>
									<div>
										<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Room Types</dt>
										<dd className="mt-1 text-gray-800">{formatRoomTypes()}</dd>
									</div>
								</dl>
							</div>

							<div className="mt-8 flex flex-col gap-4 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
								<div className="flex items-end gap-6">
									<div className="flex items-baseline gap-1.5">
										<span className="text-2xl font-semibold leading-none text-gray-950">{alertExecution.numberOfNotifications}</span>
										<span className="text-sm text-gray-500">Total Alerts</span>
									</div>
									<div className="flex items-baseline gap-1.5">
										<span className="text-2xl font-semibold leading-none text-gray-950">{notificationOutputs.length}</span>
										<span className="text-sm text-gray-500">New Alerts</span>
									</div>
								</div>
								<Link
									to={`/secured/rateshopper/rates/alert/${alertExecution.alertOutput.alert.type}/${alertExecution.alertOutput.alert.id}`}
									className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm hover:bg-blue-700">Edit Alert</Link>
							</div>
						</section>}
					{!_isEmpty(notificationOutputs) && <CompetitorsSoldOutAlertExecutionNotificationFragment companyHotel={companyHotel}
																						alertExecution={alertExecution}
																						notificationOutputs={notificationOutputs} />}
					{(!alertExecution || _isEmpty(notificationOutputs)) &&
						<div className="mx-auto max-w-5xl py-6">
							<Message state="warning">No results found</Message>
						</div>}
				</div>
			</main>
		</>;
	}
	return <></>;
};

export default CompetitorsSoldOutAlertExecutionFragment;
